import { Wrench, Package, ArrowRight, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";
import { mediaUrl } from "@/context/SettingsContext";
import { priceLabel } from "./vehicleUtils";
import { VehicleGallery } from "./VehicleDialog";
import { VehicleInterestForm } from "./VehicleInterestForm";
import { VehicleShare, nativeShare } from "./VehicleShare";

const Spec = ({ label, value }) => value ? <div className="rounded-xl bg-mint px-3 py-2"><p className="font-mono text-[9px] uppercase tracking-widest text-brand">{label}</p><p className="text-sm font-semibold text-forest">{value}</p></div> : null;

export const PartCard = ({ p, index, onOpen }) => {
  const { t } = useLanguage();
  const tv = t.vehicles;
  const tp = tv.parts;
  const wanted = p.kind === "part_wanted";
  return (
    <article data-testid={`part-card-${index}`} className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-brand/10 bg-white shadow-xl shadow-brand/5 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40">
      <button type="button" onClick={() => onOpen(p)} data-testid={`part-card-${index}-open`} className="relative block aspect-[4/3] w-full overflow-hidden bg-mint text-left">
        {p.photos?.[0] ? <img src={mediaUrl(p.photos[0])} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center text-brand/40"><Wrench className="h-16 w-16" /></div>}
        <div className="absolute left-4 top-4 flex gap-2">
          <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white ${wanted ? "bg-forest" : "bg-gold"}`}>{tv.kind[p.kind]}</span>
          <span className="rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-forest">{tp.category[p.category] || p.category}</span>
        </div>
      </button>
      <button type="button" onClick={() => nativeShare(p, tv)} data-testid={`part-card-${index}-share`} aria-label={t.news.share.label} className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-forest shadow transition-colors hover:bg-gold hover:text-white"><Share2 className="h-4 w-4" /></button>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl font-bold text-forest" data-testid={`part-card-${index}-title`}>{p.name}</h3>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-brand-hover">
          {p.compatible && <span>{p.compatible}</span>}
          <span>{tp.condition[p.condition] || p.condition}</span>
          {p.quantity != null && p.quantity !== "" && <span className="inline-flex items-center gap-1"><Package className="h-3.5 w-3.5" />{p.quantity}</span>}
        </div>
        <div className="mt-auto flex items-end justify-between gap-3 pt-6">
          <p className={`font-display text-lg font-extrabold ${wanted ? "text-forest" : "text-gold"}`} data-testid={`part-card-${index}-price`}>{wanted ? tp.wanted : priceLabel(p, tv)}</p>
          <button type="button" onClick={() => onOpen(p)} data-testid={`part-card-${index}-details`} className="inline-flex items-center gap-2 rounded-full bg-forest px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-all hover:bg-brand">{wanted ? tp.haveIt : tv.details}<ArrowRight className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </article>
  );
};

export const PartDialog = ({ part: p, onClose }) => {
  const { t } = useLanguage();
  const tv = t.vehicles;
  const tp = tv.parts;
  if (!p) return null;
  const wanted = p.kind === "part_wanted";
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-3xl p-0" data-testid="part-dialog">
        <div className="grid md:grid-cols-2">
          <div className="bg-mint p-4"><VehicleGallery v={p} testid="part-dialog" /></div>
          <div className="p-6 sm:p-8">
            <DialogHeader className="text-left">
              <span className={`w-fit rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white ${wanted ? "bg-forest" : "bg-gold"}`}>{tv.kind[p.kind]}</span>
              <DialogTitle className="font-display text-2xl font-extrabold text-forest" data-testid="part-dialog-title">{p.name}</DialogTitle>
              <DialogDescription className={`font-display text-xl font-extrabold ${wanted ? "text-forest" : "text-gold"}`} data-testid="part-dialog-price">{wanted ? tp.wanted : priceLabel(p, tv)}</DialogDescription>
            </DialogHeader>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Spec label={tp.categoryLabel} value={tp.category[p.category] || p.category} />
              <Spec label={tp.conditionLabel} value={tp.condition[p.condition] || p.condition} />
              <Spec label={tp.compatible} value={p.compatible} />
              <Spec label={tp.quantity} value={p.quantity != null && p.quantity !== "" ? String(p.quantity) : ""} />
            </div>
            {p.description && <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-brand-hover" data-testid="part-dialog-description">{p.description}</p>}
            <div className="mt-4"><VehicleShare v={p} prefix="part-dialog-share" /></div>
            <div className="mt-5">
              <VehicleInterestForm v={p} endpoint={`/parts-market/${p.id}/interest`} title={wanted ? tp.wanted : undefined} sub={wanted ? tp.wantedSub : undefined} submitLabel={wanted ? tp.haveIt : undefined} sentLabel={wanted ? tp.haveItSent : undefined} waText={wanted ? `Bonjour, je possède la pièce que vous recherchez : ${p.name}.` : `Bonjour, je suis intéressé par la pièce ${p.name} vue sur votre site.`} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
